import React from "react";

import { useAddOrUpdateCarbonProgramFeatureFlagAdmin, useGetAllFeatureFlags, useGetCarbonProgramFeatureFlags } from "utils";

import { FEATURE_FLAG_NAMES, SupportedMarket } from "consts";

import { CarbonProgram, FeatureFlagsResponse } from "models";

import { Flex, Switch, TooltipWrapper, Typography, Message as message } from "components";

const getDisabledReason = (flag: FeatureFlagsResponse, programCountry: string): string => {
  if (flag.enabledGlobally) return "This feature is enabled globally and cannot be disabled for this program";
  if (flag.enabledCountryCodes?.includes(programCountry as SupportedMarket))
    return `This feature is enabled for all farms in ${programCountry} and cannot be disabled for this program`;
  return "";
};

export const FeatureFlagsContent = ({ carbonProgram }: { carbonProgram: CarbonProgram }) => {
  const { data: allFeatureFlags, isLoading: isLoadingAllFeatureFlags } = useGetAllFeatureFlags();
  const { data: programFeatureFlags, isLoading: isLoadingProgramFeatureFlags } = useGetCarbonProgramFeatureFlags({ programId: carbonProgram.id });
  const { mutateAsync: addOrUpdateFlag } = useAddOrUpdateCarbonProgramFeatureFlagAdmin({ programId: carbonProgram.id });

  const handleToggle = async (featureFlagId: string, enabledOnProgram: boolean) => {
    try {
      await addOrUpdateFlag({ programId: carbonProgram.id, featureFlagId, enabledOnProgram });
      message({ icon: "tick", content: "Feature flag updated successfully" });
    } catch {
      message({ icon: "error", content: "Failed to update feature flag" });
    }
  };

  if (isLoadingAllFeatureFlags || isLoadingProgramFeatureFlags) {
    return <Typography variant="paragraph-m">Loading...</Typography>;
  }

  return (
    <Flex align="flex-start" gap="0.75rem" vertical>
      <Typography variant="paragraph-m" color="grey-600">
        Enable specific features for this program.
      </Typography>
      {allFeatureFlags?.map((flag) => {
        const programFlag = programFeatureFlags?.find((f) => f.featureFlagId === flag.id);
        const disabledReason = getDisabledReason(flag, carbonProgram.country);
        const isForceEnabled = !!disabledReason;
        const isEnabled = isForceEnabled || programFlag?.enabledOnProgram;

        return (
          <Flex key={flag.id} align="center" gap="0.5rem">
            <Typography variant="paragraph-m" style={{ fontSize: "0.875rem" }} color="grey-600">
              {`${FEATURE_FLAG_NAMES[flag.slug as keyof typeof FEATURE_FLAG_NAMES] || flag.slug}: ${isEnabled ? "Enabled" : "Disabled"}`}
            </Typography>
            <TooltipWrapper tooltip={{ title: disabledReason }}>
              <Switch checked={isEnabled} onChange={(enabled) => handleToggle(flag.id, enabled)} disabled={isForceEnabled} />
            </TooltipWrapper>
          </Flex>
        );
      })}
    </Flex>
  );
};
